import { Component, computed, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { InputComponent } from 'components/input/input.component';
import { Modal, ModalActionType } from 'components/modal/modal.model';
import { ModalComponent } from 'components/modal/modal.component';
import { CustomValidators } from 'weavix-shared/custom-validators';

/** A walt command that needs a typed confirmation. */
export type WaltCommand = 'update' | 'restart' | 'power-off';

const CONFIRM_WORDS: Record<WaltCommand, string> = {
    'update': 'UPDATE',
    'restart': 'RESTART',
    'power-off': 'POWER OFF',
};

/** Modal that makes the admin type the command word before a walt Update, Restart or Power Off is sent. */
@Component({
    selector: 'app-walt-command-confirm-modal',
    templateUrl: './walt-command-confirm-modal.component.html',
    styleUrls: ['./walt-command-confirm-modal.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, TranslateModule, ModalComponent, InputComponent],
})
export class WaltCommandConfirmModalComponent {
    /** The command being confirmed. */
    readonly command = input.required<WaltCommand>();
    /** Id of the walt the command targets, shown in the title. */
    readonly waltId = input.required<string>();

    /** Emits when the admin typed the command word and selected Okay. */
    readonly confirmed = output<void>();
    /** Emits when the admin cancels or closes the modal. */
    readonly closed = output<void>();

    /** Form holding the typed word; valid when it matches the command word in upper case. */
    readonly confirmationForm = computed(() => new FormGroup({
        confirmCapture: new FormControl<string | null>(null, [
            Validators.required,
            CustomValidators.stringMatch(CONFIRM_WORDS[this.command()]),
        ]),
    }));

    readonly descriptionKey = computed(() => `walt.confirm-${this.command()}-description`);
    readonly instructionKey = computed(() => `walt.confirm-${this.command()}-instruction`);

    /** Modal config with the walt id in the title and Okay disabled until the form is valid. */
    readonly modal = computed<Modal>(() => ({
        isOpen: true,
        overflow: 'visible',
        header: {
            textKey: `walt.confirm-${this.command()}`,
            textAlignment: 'left',
            showSeparator: true,
        },
        params: { waltId: this.waltId() },
        width: 841,
        actionsAlignment: 'right',
        actions: {
            submit: {
                show: true,
                textKey: 'generics.okay',
                buttonClass: 'danger',
                width: 157,
                disabled: () => this.confirmationForm().invalid,
            },
            cancel: {
                show: true,
                textKey: 'generics.cancel',
                buttonClass: 'cancel',
                width: 180,
            },
        },
    }));

    /** Emits `confirmed` on a valid submit; any other close emits `closed`. */
    handleClose(action: ModalActionType) {
        if (action === ModalActionType.submit && this.confirmationForm().valid) {
            this.confirmed.emit();
        } else if (action !== ModalActionType.submit) {
            this.closed.emit();
        }
    }
}
